// SCRUM-1850 (SCRUM-1831 slice 4): the client side of the three score-config
// routes SCRUM-1847 added (server/controllers/scoreConfig.js). One reader, the
// Scoring screen, which keeps the rows in local state — no redux slice, the
// taxonomyOverlayApi shape.
import type { AxiosResponse } from "axios";
import api from "../api";
import { asCaught } from "../caughtError";

export type ScoreCriterion = { property: string; direction: "lower" | "higher" | "boolean"; weight: number };
export type ScoreConfig = { _id: string; category: string; criteria: ScoreCriterion[]; methodVersion: string };
export type ScorePart = { property: string; value: any; partScore: number | null; weight: number };
export type ScorePreviewRow = {
  product: string;
  title: string | null;
  score: number | null;
  reason: string | null;
  parts: ScorePart[];
};

/**
 * The category's saved configuration, or null when none is saved (the route
 * answers 404 for that). Any other failure rejects.
 */
export const getScoreConfig = async (categoryId: string): Promise<ScoreConfig | null> => {
  try {
    const res: AxiosResponse<ScoreConfig> = await api.get(`/api/scoreconfig/${encodeURIComponent(categoryId)}`);
    return res.data;
  } catch (err) {
    if (asCaught(err)?.response?.status === 404) return null;
    throw err;
  }
};

/**
 * Save the criteria. Rejects with the server's answer on 403 / 422.
 */
export const saveScoreConfig = async (categoryId: string, criteria: ScoreCriterion[]): Promise<ScoreConfig> => {
  const res: AxiosResponse<ScoreConfig> = await api.put(`/api/scoreconfig/${encodeURIComponent(categoryId)}`, { criteria });
  return res.data;
};

/**
 * Score the category's products on one site with UNSAVED criteria. Writes
 * nothing server-side.
 */
export const previewScores = async (
  categoryId: string,
  siteId: string,
  criteria: ScoreCriterion[]
): Promise<ScorePreviewRow[]> => {
  const res: AxiosResponse<ScorePreviewRow[]> = await api.post(
    `/api/scoreconfig/${encodeURIComponent(categoryId)}/preview`,
    { criteria },
    { headers: { siteid: siteId } }
  );
  return Array.isArray(res.data) ? res.data : [];
};
